import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn, getUserRole } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'customer'
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const roleOptions = [
    { value: 'customer', label: 'Customer - Order Food' },
    { value: 'supplier', label: 'Supplier - Sell Food' }
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validatePassword = (password) => {
    return password?.length >= 8;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Validate inputs
    const newErrors = {};
    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    console.log('=== LOGIN ATTEMPT START ===');
    console.log('Email:', formData?.email);
    console.log('signIn function type:', typeof signIn);
    console.log('signIn function:', signIn);

    try {
      // Verify signIn function exists and is callable
      if (!signIn || typeof signIn !== 'function') {
        console.error('signIn is not available:', { signIn, type: typeof signIn });
        setErrors({
          submit: 'Authentication system is not ready. Please refresh the page and try again.'
        });
        setIsLoading(false);
        return;
      }

      // Use real Supabase authentication
      console.log('Calling signIn...');
      const result = await signIn(formData?.email, formData?.password);
      
      if (!result) {
        console.error('signIn returned null/undefined');
        setErrors({
          submit: 'Authentication failed. Please try again.'
        });
        setIsLoading(false);
        return;
      }

      const { data, error } = result;
      console.log('SignIn response:', { data: data ? 'received' : 'null', error: error ? error?.message : 'none' });

      if (error) {
        console.error('SignIn error:', error);
        // Check for specific error types
        let errorMessage = 'Invalid credentials. Please check your email and password.';
        
        if (error?.message?.toLowerCase()?.includes('email not confirmed')) {
          errorMessage = 'Please confirm your email address before logging in. Check your inbox for the confirmation link.';
        } else if (error?.message?.toLowerCase()?.includes('invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please try again or create a new account.';
        } else if (error?.message) {
          errorMessage = error?.message;
        }
        
        setErrors({
          submit: errorMessage
        });
        setIsLoading(false);
        return;
      }

      if (data?.user) {
        console.log('User authenticated:', data?.user?.id);
        console.log('Email confirmed at:', data?.user?.email_confirmed_at);
        
        // Check if email is confirmed
        if (data?.user?.email_confirmed_at === null) {
          console.warn('Email not confirmed');
          setErrors({
            submit: 'Please confirm your email address before logging in. Check your inbox for the confirmation link.'
          });
          setIsLoading(false);
          return;
        }
        
        // Get user role from database
        console.log('Fetching user role from database...');
        const userRole = await getUserRole();
        console.log('User role retrieved:', userRole);
        
        if (!userRole) {
          console.error('Failed to retrieve user role from database');
          setErrors({
            submit: 'Unable to retrieve your account information. Please try again or contact support.'
          });
          setIsLoading(false);
          return;
        }
        
        // Navigate based on actual user role from database
        console.log('Navigating to dashboard for role:', userRole);
        if (userRole === 'customer') {
          navigate('/customer-dashboard');
        } else if (userRole === 'supplier') {
          navigate('/supplier-dashboard');
        } else if (userRole === 'admin') {
          navigate('/admin-dashboard');
        } else {
          console.warn('Unknown role, defaulting to customer dashboard');
          // Fallback to customer dashboard if role not found
          navigate('/customer-dashboard');
        }
        console.log('=== LOGIN ATTEMPT SUCCESS ===');
      } else {
        console.error('No user object in response');
        setErrors({
          submit: 'Login failed. Please try again.'
        });
      }
    } catch (err) {
      console.error('Login exception:', err);
      setErrors({
        submit: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Select
        label="I want to"
        options={roleOptions}
        value={formData?.role}
        onChange={(value) => handleInputChange('role', value)}
        required
      />
      <Input
        type="email"
        label="Email Address"
        placeholder="Enter your email"
        value={formData?.email}
        onChange={(e) => handleInputChange('email', e?.target?.value)}
        error={errors?.email}
        required
      />
      <div className="relative">
        <Input
          type={showPassword ? 'text' : 'password'}
          label="Password"
          placeholder="Enter your password"
          value={formData?.password}
          onChange={(e) => handleInputChange('password', e?.target?.value)}
          error={errors?.password}
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-[38px] p-1 hover:bg-muted rounded transition-smooth"
        >
          <Icon 
            name={showPassword ? 'EyeOff' : 'Eye'} 
            size={18} 
            color="var(--color-muted-foreground)" 
          />
        </button>
      </div>
      {errors?.submit && (
        <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
          <p className="text-sm text-destructive flex-1">{errors?.submit}</p>
        </div>
      )}
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        iconName="LogIn"
        iconPosition="right"
      >
        Sign In
      </Button>
      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={() => navigate('/forgot-password')}
          className="text-sm text-primary hover:underline transition-smooth"
        >
          Forgot Password?
        </button>
        <button
          type="button"
          onClick={() => navigate('/register')}
          className="text-sm text-primary hover:underline transition-smooth"
        >
          Create Account
        </button>
      </div>
    </form>
  );
};

export default LoginForm;